import {INTERVAL_24, LASTSHOW_COOKIE_NAME, TESTPERSIST_COOKIE_NAME, CONSENT_COOKIE_NAME, SERVERSIDE_REFRESHER_COOKIE} from '../constants';
import {logger} from './logger';
import {isSafari} from '../utils/sbrowser';

const cookieNames = [CONSENT_COOKIE_NAME, LASTSHOW_COOKIE_NAME, TESTPERSIST_COOKIE_NAME];

/**
 * Sets cookie (example from w3schools).
 * @param {*} value cookie value
 * @param {string} name cookie name
 * @param {number} expiracyInDays
 */
export const setCookie = (value, name, expiracyInDays = 365) => {
  const date = new Date();
  const {hostname, protocol} = location;

  date.setTime(date.getTime() + expiracyInDays * INTERVAL_24);
  const expires = expiracyInDays < 0 ? '' : ';expires=' + date.toUTCString();
  const cookie = name + '=' + value + expires + ';path=/';

  // localhost specific (testing)
  if (hostname === 'localhost') {
    document.cookie = cookie;
    return;
  }

  const domain = hostname
    .split('.')
    .slice(-2)
    .join('.');
  const secure = protocol.endsWith('s:') ? ';secure' : '';

  document.cookie = cookie + ';domain=.' + domain + secure;
};

/**
 * Gets cookie (example from w3schools).
 * @param {string} name
 * @returns {string}
 */
export const getCookie = name => {
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(';');

  const cookie = cookies.find(_cookie => _cookie.trim().startsWith(name));
  return cookie ? cookie.trim().substring(name.length + 1, cookie.length) : '';
};

/**
 * Deletes cookie.
 * @param {string} name
 */
export const deleteCookie = name => setCookie(0, name, 0);

/**
 * Check if cookie exists.
 * @param {string} name
 * @return {boolean}
 */
export const existsCookie = name => getCookie(name) !== '';

/**
 * Resaves cookie in Safari if exists.
 * @param {string} name
 * @param {boolean} notBrowserSpecific
 */
export const resaveCookie = (name, notBrowserSpecific = false) => {
  if (getCookie(SERVERSIDE_REFRESHER_COOKIE)) {
    logger(902, '(Safari) Resaved cookie is done by server side refresher.');
    return; // server side resaving is enabled if exists
  }

  const value = getCookie(name);

  if (value && (notBrowserSpecific || isSafari)) {
    deleteCookie(name);
    setCookie(value, name);
    logger(900, '(Safari) Resaved cookie.');
  }
};

export const removeOldCookie = cname => {
  const date = new Date();
  let cookie = cname + '=1; expires=' + date.toUTCString() + ';path=/';
  const {hostname, protocol} = location;

  document.cookie = cookie;

  const domain = hostname
    .split('.')
    .slice(-2)
    .join('.');

  if (domain !== hostname) {
    const secure = protocol.endsWith('s:') ? ';secure' : '';
    cookie += ';domain=.' + hostname;

    document.cookie = cookie;
    document.cookie = cookie + secure;
  }
};

const popCookieNameAndValue = (cookieStrings = []) => {
  const [name, ...values] = cookieStrings
    .pop()
    .trim()
    .split('=');
  return {name, value: values.join('=')};
};

const getCookies = () => {
  const cookieStrings = decodeURIComponent(document.cookie).split(';');
  const cookies = [];

  while (0 < cookieStrings.length) {
    const cookie = popCookieNameAndValue(cookieStrings);
    if (cookieNames.includes(cookie.name)) {
      cookies.push(cookie);
    }
  }

  return cookies;
};

const returnNewer = (timedata1, timedata2) => (timedata1.split('|')[0] > timedata2.split('|')[0] ? timedata1 : timedata2);

function refreshCookies() {
  if (cookieNames.every(name => !document.cookie.includes(name))) {
    logger(901, 'Cookie refresh not needed');
    return;
  }

  const allCookies = getCookies();

  cookieNames.forEach(name => removeOldCookie(name));

  const newCookies = getCookies();

  allCookies.forEach(all => {
    if (newCookies.some(({name}) => name === all.name)) {
      newCookies.forEach(({name, value}) => {
        if (name === all.name && value !== all.value) {
          //retrieve old value
          if (name === CONSENT_COOKIE_NAME) {
            setCookie(all.value, name);
          } else {
            setCookie(returnNewer(value, all.value), name);
          }
        }
      });
    } else {
      // old cookie not yet in new domain
      setCookie(all.value, all.name);
    }
  });

  cookieNames.forEach(name => resaveCookie(name));
}

refreshCookies();
